import React from 'react';
import PropTypes from 'prop-types';

import HeaderRowContainer from '../containers/header_row_container';
import AggregateRowContainer from '../containers/aggregate_row_container';
import useSortState from '../hooks/useSortState';
import { sortAggregates } from '../utils/sort_utils';
import { formatCurrency } from '../utils/currency_utils';
import { VIEW_MODES } from '../constants';
import { componentText } from '../utils/i18n_utils';
import { hasVisibleStartingHoleMarker } from '../utils/thru_utils';

const t = (key, fallback) => componentText(`leaderboard_table.${key}`, fallback);
import useThruSeparators from '../hooks/useThruSeparators'

// Manager site only: no favourite column there, so the Pos column widens by
// this much to keep the number clear of the name (RTM-10239).
const POSITION_GAP_WITHOUT_FAVORITE = 16

const WEB_COLUMN_WIDTHS = {
  position: 40,
  favorite: 40,
  move: 60,
  flags: 46,
  name: 150,
  data: 60,
};

const MOBILE_COLUMN_WIDTHS = {
  position: 52,
  favorite: 32,
  move: 0,
  flags: 27,
};

const LeaderboardTable = ({
  eventId,
  resultScope,
  favorites = [],
  isFavorite,
  displayedFlags,
  columns = [],
  rounds = {},
  displayCut,
  cutListPosition = '',
  cutListLabel = null,
  resolveTiesHref = null,
  viewMode,
  isManagerSite,
  hasPositionColumn = false,
  sectionLines = {},
  totalPurseAllocated,
  totalPointsAllocated,
  currencySymbol,
  purseDecimalPlaces,
}) => {
  const sortCacheKey = `${eventId}_${isFavorite ? 'fav' : 'full'}`;
  const { sortKey, sortDir, toggleSort } = useSortState(sortCacheKey);

  // Cut/section lines are placed by row index, which only matches the
  // backend's ordering while the default sort (position ascending) is active.
  const isDefaultOrder = sortKey === 'position' && sortDir === 'asc';

  const rows = React.useMemo(() => {
    const scope = resultScope || [];
    // Filter to favorites before sorting so the favorites table sorts only its
    // handful of rows, not the whole field (filter and sort commute — same result).
    const filtered = isFavorite
      ? scope.filter((agg) => favorites.includes(agg?.memberIds?.[0]))
      : scope;
    const sorted = isDefaultOrder ? filtered : sortAggregates(filtered, sortKey, sortDir);
    return sorted.map((agg) => [agg.id, agg]);
  }, [resultScope, favorites, isFavorite, sortKey, sortDir]);

  const hasMoveColumn = React.useMemo(() => {
    if (viewMode === VIEW_MODES.MOBILE) return false;
    return (resultScope || []).some((agg) => agg?.posChange !== undefined);
  }, [resultScope, viewMode]);

  // A trailing "*" on a rendered thru value (e.g. "3:00 PM*") marks a player
  // starting on hole 10; the note row at the end of the table explains it and
  // leaves with the Thru cells once the round completes (RTM-10539).
  const hasStartingHoleNote = React.useMemo(() => {
    if (isFavorite) return false;
    return hasVisibleStartingHoleMarker(resultScope, columns, rounds);
  }, [resultScope, columns, rounds, isFavorite]);

  const tableRef = React.useRef(null)
  useThruSeparators(tableRef)

  const tableStyle = React.useMemo(() => {
    const hasFlags = ['state_flags', 'country_flags'].includes(displayedFlags);

    const positionGap = isManagerSite ? POSITION_GAP_WITHOUT_FAVORITE : 0
    const mobilePositionGap = hasFlags ? 0 : positionGap
    const widths = viewMode === VIEW_MODES.MOBILE
      ? {
          position: hasPositionColumn ? MOBILE_COLUMN_WIDTHS.position + mobilePositionGap : 0,
          favorite: !isManagerSite ? MOBILE_COLUMN_WIDTHS.favorite : 0,
          move: MOBILE_COLUMN_WIDTHS.move,
          flags: hasFlags ? MOBILE_COLUMN_WIDTHS.flags : 0,
        }
      : {
          position: hasPositionColumn ? WEB_COLUMN_WIDTHS.position + positionGap : 0,
          favorite: !isManagerSite ? WEB_COLUMN_WIDTHS.favorite : 0,
          move: hasMoveColumn ? WEB_COLUMN_WIDTHS.move : 0,
          flags: hasFlags ? WEB_COLUMN_WIDTHS.flags : 0,
        };

    const favoriteLeft = widths.position;
    const moveLeft = favoriteLeft + widths.favorite;
    const flagsLeft = moveLeft + widths.move;
    const nameLeft = flagsLeft + widths.flags;

    return {
      '--position-width': `${widths.position}px`,
      '--favorite-left': `${favoriteLeft}px`,
      '--move-left': `${moveLeft}px`,
      '--flags-left': `${flagsLeft}px`,
      '--flags-width': `${widths.flags}px`,
      '--name-left': `${nameLeft}px`,
    };
  }, [viewMode, hasPositionColumn, isManagerSite, hasMoveColumn, displayedFlags]);

  return (
    <table ref={tableRef} className={`lbv2__leaderboard-table ${isFavorite ? 'lbv2__favorites-table' : ''}`} style={tableStyle}>
      <thead>
        <HeaderRowContainer
          eventId={eventId}
          viewMode={viewMode}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={toggleSort}
        />
      </thead>
      <tbody>
        {rows.map(([aggId, agg], index) => (
          <AggregateRowContainer
            key={aggId}
            eventId={eventId}
            aggId={aggId}
            agg={agg}
            viewMode={viewMode}
            displayCutLine={isDefaultOrder && index === cutListPosition && displayCut}
            cutListLabel={cutListLabel}
            resolveTiesHref={resolveTiesHref}
            sectionLine={isDefaultOrder ? sectionLines[index] : undefined}
          />
        ))}
        {totalPurseAllocated > 0 && (
          <tr className="lbv2__total-purse-row">
            <td className="lbv2__total-purse sticky" colSpan={100}>
              <div className="lbv2__total-purse-text">
                {t('total_purse_allocated', 'Total Purse Allocated:')} {formatCurrency(totalPurseAllocated, currencySymbol, purseDecimalPlaces)}
              </div>
            </td>
          </tr>
        )}
        {totalPointsAllocated > 0 && (
          <tr className="lbv2__total-points-row">
            <td className="lbv2__total-points sticky" colSpan={100}>
              <div className="lbv2__total-points-text">
                {t('total_points_allocated', 'Total Points Allocated:')} {totalPointsAllocated}
              </div>
            </td>
          </tr>
        )}
        {hasStartingHoleNote && (
          <tr className="lbv2__starting-hole-note-row">
            <td className="lbv2__starting-hole-note sticky" colSpan={100}>
              <div className="lbv2__starting-hole-note-text">
                {t('starting_hole_note', 'Note: Players start on holes 1 and 10. Players starting on 10 are denoted with an asterisk.')}
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

LeaderboardTable.propTypes = {
  eventId: PropTypes.string.isRequired,
  resultScope: PropTypes.array,
  favorites: PropTypes.array,
  isFavorite: PropTypes.bool.isRequired,
  displayedFlags: PropTypes.string,
  columns: PropTypes.array,
  rounds: PropTypes.object,
  displayCut: PropTypes.bool,
  cutListPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cutListLabel: PropTypes.object,
  resolveTiesHref: PropTypes.string,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  isManagerSite: PropTypes.bool,
  hasPositionColumn: PropTypes.bool,
  sectionLines: PropTypes.object,
  totalPurseAllocated: PropTypes.number,
  totalPointsAllocated: PropTypes.number,
  currencySymbol: PropTypes.string,
  purseDecimalPlaces: PropTypes.number,
};

export default LeaderboardTable;
